/** US Eastern Time (EST/EDT per yearly DST rules). All data entry uses this zone. */
export const APP_TIMEZONE = 'America/New_York';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export type EasternParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/** Calendar date/time components in US Eastern for the given instant (default: now). */
export function getEasternParts(date: Date = new Date()): EasternParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    parseInt(parts.find(p => p.type === type)?.value ?? '0', 10);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** Today's date as YYYY-MM-DD in US Eastern. */
export function easternTodayYmd(date: Date = new Date()): string {
  const { year, month, day } = getEasternParts(date);
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** YYYY-MM-DD for a Date interpreted in US Eastern. */
export function dateToYmdInEastern(d: Date): string {
  const { year, month, day } = getEasternParts(d);
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** YYYY-MM-DD → local Date for calendar pickers (no timezone shift). */
export function ymdToLocalDate(ymd: string | null | undefined): Date | null {
  if (!ymd?.trim()) return null;
  const [y, mo, d] = ymd.split('T')[0].split('-').map(Number);
  if ([y, mo, d].some(n => Number.isNaN(n))) return null;
  return new Date(y, mo - 1, d);
}

/**
 * Date whose get* methods reflect Eastern wall clock — for MUI pickers and legacy Date code
 * that reads local components without timezone conversion.
 */
export function easternWallClockDate(date: Date = new Date()): Date {
  const p = getEasternParts(date);
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, 0);
}

/** Value for HTML datetime-local inputs (YYYY-MM-DDTHH:mm) in US Eastern. */
export function easternDatetimeLocalValue(date: Date = new Date()): string {
  const p = getEasternParts(date);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** Parse stored datetime string (no timezone) into Eastern wall-clock parts. */
export function parseStoredDatetimeParts(stored: string | null | undefined): EasternParts | null {
  if (!stored?.trim()) return null;
  const normalized = stored.trim().replace(' ', 'T');
  const [datePart, timePart = '00:00:00'] = normalized.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h = 0, mi = 0, s = 0] = timePart.split(':').map(Number);
  if ([y, mo, d, h, mi, s].some(n => Number.isNaN(n))) return null;
  return { year: y, month: mo, day: d, hour: h, minute: mi, second: s };
}

/** Format 24h hour/minute as 12h AM/PM. */
export function formatTime12hFrom24(hour24: number, minute: number): string {
  const ap = hour24 >= 12 ? 'PM' : 'AM';
  let h = hour24 % 12;
  if (h === 0) h = 12;
  return `${h}:${pad2(minute)} ${ap}`;
}

/** Format stored datetime time portion as 12h AM/PM (no timezone conversion). */
export function formatTimeAmPmFromStored(stored: string | null | undefined): string {
  const parts = parseStoredDatetimeParts(stored);
  if (!parts) return '';
  return formatTime12hFrom24(parts.hour, parts.minute);
}

/** datetime-local display value from stored datetime (Eastern wall clock). */
export function storedToDatetimeLocalValue(stored?: string | null): string {
  const parts = parseStoredDatetimeParts(stored);
  if (!parts) return '';
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}T${pad2(parts.hour)}:${pad2(parts.minute)}`;
}
